import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Factors() {
  const { t } = useLang();
  const [factors, setFactors] = useState([]);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    scope: 1,
    category: "",
    subcategory: "",
    name: "",
    unit: "",
    factor: "",
    source: "Internal",
    region: "Global",
  });

  const load = () => {
    const params = {};
    if (scope) params.scope = scope;
    if (q) params.q = q;
    api.get(`/factors`, { params }).then((r) => setFactors(r.data));
  };

  useEffect(() => { load(); }, [q, scope]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/factors", { ...form, scope: parseInt(form.scope), factor: parseFloat(form.factor) });
      toast.success(t("factorCreated"));
      setShowForm(false);
      setForm({ ...form, name: "", unit: "", factor: "", category: "", subcategory: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/factors/${id}`);
    toast.success(t("factorDeleted"));
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Library</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("factors")}</h1>
          <p className="text-muted-foreground text-sm mt-1">IPCC 2006 · DEFRA 2023 · IEA · EPA eGRID · PCAF · Custom</p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} data-testid="factor-toggle">
          <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("newFactor")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="factor-form">
              <div className="space-y-2">
                <Label>{t("scope")}</Label>
                <Select value={String(form.scope)} onValueChange={(v) => setForm({ ...form, scope: parseInt(v) })}>
                  <SelectTrigger data-testid="factor-form-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3].map((s) => <SelectItem key={s} value={String(s)}>Scope {s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("category")}</Label>
                <Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="factor-form-cat" />
              </div>
              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="factor-form-subcat" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="factor-form-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("unit")}</Label>
                <Input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kWh / L / kg" data-testid="factor-form-unit" />
              </div>
              <div className="space-y-2">
                <Label>{t("factorValue")}</Label>
                <Input type="number" step="any" required value={form.factor} onChange={(e) => setForm({ ...form, factor: e.target.value })} data-testid="factor-form-value" />
              </div>
              <div className="space-y-2">
                <Label>{t("source")}</Label>
                <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="factor-form-source" />
              </div>
              <div className="space-y-2">
                <Label>Region</Label>
                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="factor-form-region" />
              </div>
              <div className="md:col-span-3">
                <Button type="submit" data-testid="factor-form-submit">{t("addCustomFactor")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="factor-search" />
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
          {[0, 1, 2, 3].map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 font-mono-data ${scope === s ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
              data-testid={`factor-scope-${s}`}
            >
              {s === 0 ? t("all") : `S${s}`}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">{t("scope")}</th>
                  <th className="text-left px-4 py-2">{t("category")}</th>
                  <th className="text-left px-4 py-2">{t("factor")}</th>
                  <th className="text-left px-4 py-2">{t("unit")}</th>
                  <th className="text-right px-4 py-2">{t("value")} (kgCO₂e)</th>
                  <th className="text-left px-4 py-2">{t("source")}</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {factors.map((f) => (
                  <tr key={f.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-2 font-mono-data">S{f.scope}</td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">{f.category}</td>
                    <td className="px-4 py-2">
                      {f.name}
                      {f.custom && <span className="ml-2 text-[10px] font-mono-data uppercase bg-accent/10 text-accent px-1.5 py-0.5 rounded">{t("customFactor")}</span>}
                    </td>
                    <td className="px-4 py-2 font-mono-data text-xs">{f.unit}</td>
                    <td className="px-4 py-2 text-right font-mono-data font-medium">{f.factor}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{f.source}</td>
                    <td className="px-2 py-2 text-right">
                      {f.custom && (
                        <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`factor-delete-${f.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
